/**
 * reCAPTCHA v3 composable for client-side token generation
 *
 * Usage:
 * const { executeRecaptcha, isReady } = useRecaptcha()
 * const token = await executeRecaptcha('submit_form')
 */

// Shared across every useRecaptcha() instance so concurrent forms (hero +
// modal + footer) trigger exactly one network request and one Promise
// instead of N pollers racing each other.
let scriptLoadPromise: Promise<void> | null = null

function ensureScriptLoaded(siteKey: string): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('reCAPTCHA cannot load server-side'))
  }
  if (scriptLoadPromise) return scriptLoadPromise

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    if (window.grecaptcha?.ready) {
      window.grecaptcha.ready(() => resolve())
      return
    }

    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
    script.async = true
    script.defer = true
    script.onload = () => window.grecaptcha.ready(() => resolve())
    script.onerror = () => {
      // Reset so a subsequent call can retry rather than rejecting forever
      // from the cached promise after a transient network failure.
      scriptLoadPromise = null
      reject(new Error('Failed to load reCAPTCHA script'))
    }
    document.head.appendChild(script)
  })

  return scriptLoadPromise
}

export function useRecaptcha() {
  const config = useRuntimeConfig()
  const siteKey = config.public.recaptchaSiteKey
  const isReady = ref(false)
  const isLoading = ref(false)

  const loadScript = (): Promise<void> => {
    return ensureScriptLoaded(siteKey).then(() => {
      isReady.value = true
    })
  }

  const executeRecaptcha = async (action: string): Promise<string | null> => {
    if (!siteKey) {
      console.warn('[reCAPTCHA] No site key configured')
      return null
    }

    try {
      isLoading.value = true

      if (!isReady.value) {
        await loadScript()
      }

      const token = await window.grecaptcha.execute(siteKey, { action })
      return token
    } catch (error) {
      console.error('[reCAPTCHA] Error executing:', error)
      return null
    } finally {
      isLoading.value = false
    }
  }

  // Load reCAPTCHA on the first sign of user engagement (focusin / click /
  // keydown) rather than on idle, so the script's main-thread work does not
  // land inside PSI's TBT window (default mobile audit does not synthesize
  // interaction). click is preferred over pointerdown so a touch-scroll
  // gesture does not trigger the load on mobile. executeRecaptcha() still
  // loads the script on demand if a user submits before any interaction
  // event fires.
  let teardownListeners: (() => void) | null = null

  onMounted(() => {
    if (!siteKey || !import.meta.client) return

    let triggered = false
    const prefetch = () => {
      if (triggered) return
      triggered = true
      teardownListeners?.()
      loadScript().catch(console.error)
    }

    document.addEventListener('focusin', prefetch, { passive: true })
    document.addEventListener('click', prefetch, { passive: true })
    document.addEventListener('keydown', prefetch, { passive: true })

    teardownListeners = () => {
      document.removeEventListener('focusin', prefetch)
      document.removeEventListener('click', prefetch)
      document.removeEventListener('keydown', prefetch)
      teardownListeners = null
    }
  })

  onBeforeUnmount(() => {
    teardownListeners?.()
  })

  return {
    executeRecaptcha,
    isReady: readonly(isReady),
    isLoading: readonly(isLoading),
  }
}

// Type declaration for grecaptcha
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}
